<template>
	<v-row no-gutters class="pa-2 overflow-auto" style="height: 100%">
		<!-- Time tracking of the user in this page -->
		<UserTimeTracker page-key="ccmVitalEntry" />

		<v-col cols="12" class="pa-1" style="height: 100%">
			<v-card style="height: 100%; min-height: 300px" :loading="dataLoading">
				<v-card-title class="py-2 py-lg-4">
					<v-row no-gutters>
						<v-col class="text-left" :class="$vuetify.breakpoint.mdAndDown ? 'font-responsive-md' : ''">
							{{ $t('vital-entries') }}
						</v-col>
						<v-spacer />
						<v-col class="d-flex justify-end ga-2">
							<v-btn
								v-if="!dataLoading"
								small
								rounded-lg
								color="primary2 login-button"
								class="mr-1"
								@click="isViewGraph = !isViewGraph"
								style="max-width: 200px; min-width: 150px">
								<v-icon left> mdi-chart-line </v-icon>
								<span style="text-transform: none">{{ isViewGraph ? $t('hide-graph') : $t('view-graph') }}</span>
							</v-btn>
							<ActionButton
								icon="mdi-file-delimited"
								tooltip="download-csv"
								:maxSize="25"
								:minSize="20"
								@clicked="downloadCSV()"
								:busy="downloading"
								class="mr-1" />
							<v-icon
								color="primary"
								@click="synchronizeAddDrawers('ccmVitalEntryAdd')"
								:size="$vuetify.breakpoint.mdAndDown ? 20 : 25">
								mdi-plus-circle
							</v-icon>
						</v-col>
					</v-row>
				</v-card-title>
				<v-card-text
					class="overflow-auto"
					:style="$vuetify.breakpoint.lgAndUp ? 'height: calc(100% - 64px)' : 'height: calc(100% - 48px)'">
					<DataGraph v-if="isViewGraph" ref="vitalsGraph" :rawData="vitalEntries" />
					<v-data-table
						v-else
						:items="vitalEntries"
						:headers="entryTableHeaders"
						:items-per-page="-1"
						style="overflow-y: auto"
						:height="pageHeight($vuetify.breakpoint.mdAndDown ? 93 : 77)"
						fixed-header
						dense
						class="data-table-outline"
						:footer-props="{ 'items-per-page-text': '' }">
						<template v-slot:item="{ item, index }">
							<v-tooltip left>
								<template v-slot:activator="{ on, attrs }">
									<tr v-on="on" v-bind="attrs" class="cursor" :class="index % 2 ? 'odd-row' : 'even-row'">
										<td class="text-left px-1">
											{{ item.vitalType }}
										</td>
										<td class="text-left px-1">
											{{ item.value }}
										</td>
										<td class="text-left px-1">
											{{ item.performedBy }}
										</td>
										<td class="text-left px-1">
											{{ formatTimestamp(item.vitalDate, 'datetime_med') }}
										</td>
										<td class="text-left px-1">
											{{ formatDuration(item.timeSpentSeconds) }}
										</td>
										<td class="px-1 text-right">
											<span v-if="item.ccmRecordId === selectedCCMRecord.id">
												<v-tooltip left>
													<template v-slot:activator="{ on }">
														<v-icon
															class="mt-1"
															color="primary"
															:size="$vuetify.breakpoint.mdAndDown ? 20 : 25"
															v-on="on"
															@click.stop="synchronizeEditDrawers('ccmVitalEntryEdit', item.id)">
															mdi-pencil
														</v-icon>
													</template>
													{{ $t('edit') }}
												</v-tooltip>
												<ActionButton
													icon="mdi-delete"
													tooltip="delete"
													:maxSize="25"
													:minSize="20"
													@clicked="
														deleteId = item.id;
														showDeleteDialog = true;
													"
													:busy="deletingIds.includes(item.id)" />
											</span>
										</td>
									</tr>
								</template>
								<div style="max-width: 350px">{{ item.notes }}</div>
							</v-tooltip>
						</template>
						<template v-slot:[`footer.prepend`]>
							<v-row dense class="ma-0">
								<v-col class="text-left">
									<p class="ma-0">
										<v-tooltip top>
											<template v-slot:activator="{ on }">
												<v-icon class="ml-1" color="primary" size="25" v-on="on"> mdi-help-circle-outline </v-icon>
											</template>
											{{ $t('need-to-select-the-correct-record') }}
										</v-tooltip>
									</p>
								</v-col>
							</v-row>
						</template>
					</v-data-table>
				</v-card-text>
			</v-card>
		</v-col>

		<DeleteDialog
			:dialog.sync="showDeleteDialog"
			@confirm="
				showDeleteDialog = false;
				deletingIds.push(deleteId);
				deleteCCMVitalEntryEvent(deleteId);
				deleteId = -1;
			" />
	</v-row>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import { formatTimestamp, formatDuration, downloadCSVWithKeys, getFullName } from '@/utils/utilities';
import DeleteDialog from '@/views/dialogs/DeleteDialog.vue';
import DataGraph from '../DataGraph.vue';
import UserTimeTracker from '@/views/Client/components/UserTimeTracker.vue';
import ActionButton from '../ActionButton.vue';

export default {
	name: 'CCMVitalEntry',

	components: {
		DeleteDialog,
		DataGraph,
		UserTimeTracker,
		ActionButton,
	},

	data () {
		return {
			isViewGraph: false,
			deleteId: -1,
			deletingIds: [],
			showDeleteDialog: false,
			downloading: false,
		};
	},

	computed: {
		...mapState({
			detailedUser: (state) => state.users.detailedUser,
			vitalEntries: (state) => state.ccmVitalEntry.ccmVitalEntriesByPatientId,
			dataLoading: (state) => state.ccmVitalEntry.ccmVitalEntriesLoadingStatus,
			selectedCCMRecord: (state) => state.ccmRecords.ccmRecord,
		}),
		...mapGetters({
			pageHeight: 'pageHeight',
		}),
		entryTableHeaders () {
			return [
				{
					text: this.$t('vital-type'),
					value: 'vitalType',
					class: 'px-1',
				},
				{
					text: this.$t('value'),
					value: 'value',
					class: 'px-1',
				},
				{
					text: this.$t('performed-by'),
					value: 'performedBy',
					class: 'px-1',
				},
				{
					text: this.$t('date'),
					value: 'vitalDate',
					class: 'px-1',
				},
				{
					text: this.$t('time-spent-header'),
					value: 'timeSpentSeconds',
					sortable: false,
					class: 'px-1',
				},

				{
					text: this.$t('actions'),
					sortable: false,
					value: '',
					align: 'right',
					class: 'pl-1 pr-3',
				},
			];
		},
	},

	methods: {
		...mapActions({
			getCCMVitalEntries: 'ccmVitalEntry/getCCMVitalEntriesByPatientId',
			getSelectedCCMVitalEntry: 'ccmVitalEntry/getCCMVitalEntryById',
			deleteCCMVitalEntry: 'ccmVitalEntry/deleteCCMVitalEntry',
			getAllVitalTypeOptions: 'ccmRecords/getVitalTypeOptionTemplate',
			storeCCMRecordsByPatientId: 'ccmRecords/storeMinimalCCMRecordsByPatientId',
		}),
		formatTimestamp,
		formatDuration,
		downloadCSVWithKeys,
		getFullName,

		synchronizeAddDrawers (drawer) {
			const parameters = {
				detailsDrawer: true,
				page: drawer,
			};
			this.$store.commit('SET_DETAILS_DRAWER', parameters);
		},
		async deleteCCMVitalEntryEvent (id) {
			await this.deleteCCMVitalEntry(id);
			await this.storeCCMRecordsByPatientId(this.selectedCCMRecord.patientId);
			this.deletingIds = this.deletingIds.filter((item) => item !== id);
		},
		async synchronizeEditDrawers (drawer, id) {
			await this.getSelectedCCMVitalEntry(id);
			this.$store.commit('SET_PATIENT_DETAILS_DRAWER', drawer);
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', {
				updateDrawer: true,
				page2: 'ccmDetails',
			});
		},
		downloadCSV () {
			this.downloading = true;
			downloadCSVWithKeys(this.vitalEntries, 'Vital_Entries_for_' + getFullName(this.detailedUser.userData), {
				ids: ['ccmRecordId'],
				data: ['vitalType', 'value', 'performedBy', 'performedByRole', 'dataSupplyProviderName'],
				timestamps: ['vitalDate'],
				timeSpent: ['timeSpentSeconds'],
			});
			this.downloading = false;
		},
	},

	async mounted () {
		this.$store.commit('ccmVitalEntry/STORE_CCM_VITAL_ENTRIES_BY_PATIENT_ID', []);
		await this.getCCMVitalEntries(this.detailedUser.userData.id);
	},
};
</script>
